'use client';

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '@/contexts/NotificationContext';

export type BuyingSuggestionRow = {
  inventoryItemId: number;
  locationId: number;
  onHandQty: number;
  openPoQty: number;
  effectiveAvail: number;
  suggestedBuyQty: number;
  triggerBuy: boolean;
  reason: string | null;
  estimatedCostCents: number | null;
  reorderPoint: number;
  targetQty: number;
  minQty: number;
  preferredVendorId?: number | null;
  inventoryItem?: { id: number; upc: string; name?: string | null };
  location?: { id: number; name?: string | null };
};

type Props = {
  businessUnit: 'tv' | 'ia';
  locations: Array<{ id: number; name?: string | null }>;
  vendors: Array<{ id: number; name: string }>;
  fetchSuggestions: (opts: { locationId?: number; triggerOnly?: boolean }) => Promise<BuyingSuggestionRow[]>;
  createDraftPo: (payload: {
    vendorId: number;
    receiveLocationId: number;
    poNumber: string;
    lines: Array<{ inventoryItemId: number; orderedQty: number; unitCostCents?: number }>;
  }) => Promise<{ id: number }>;
  poDetailPath: (poId: number) => string;
};

const BuyingSuggestionsPanel: React.FC<Props> = ({
  businessUnit,
  locations,
  vendors,
  fetchSuggestions,
  createDraftPo,
  poDetailPath,
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [locationId, setLocationId] = useState<number | ''>('');
  const [triggerOnly, setTriggerOnly] = useState(true);
  const [rows, setRows] = useState<BuyingSuggestionRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSuggestions({
        locationId: locationId === '' ? undefined : locationId,
        triggerOnly,
      });
      setRows(data);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, [fetchSuggestions, locationId, triggerOnly, showError]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const onCreatePo = useCallback(async (row: BuyingSuggestionRow) => {
    const vendorId = row.preferredVendorId ?? vendors[0]?.id;
    if (!vendorId) {
      showError('Select a preferred vendor on replenishment policy, or add a vendor first.');
      return;
    }
    if (row.suggestedBuyQty <= 0) {
      showError('Nothing to buy for this line.');
      return;
    }
    try {
      const prefix = businessUnit === 'tv' ? 'TV' : 'IA';
      const po = await createDraftPo({
        vendorId,
        receiveLocationId: row.locationId,
        poNumber: `${prefix}-SUG-${Date.now()}`,
        lines: [
          {
            inventoryItemId: row.inventoryItemId,
            orderedQty: row.suggestedBuyQty,
            unitCostCents: row.estimatedCostCents != null ? Math.round(row.estimatedCostCents / row.suggestedBuyQty) : undefined,
          },
        ],
      });
      showSuccess('Draft PO created');
      navigate(poDetailPath(po.id));
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to create PO');
    }
  }, [businessUnit, vendors, createDraftPo, navigate, poDetailPath, showError, showSuccess]);

  const columns = useMemo<MRT_ColumnDef<BuyingSuggestionRow>[]>(
    () => [
      {
        accessorKey: 'location.name',
        header: 'Location',
        Cell: ({ row }) => row.original.location?.name ?? row.original.locationId,
      },
      {
        accessorKey: 'inventoryItem.upc',
        header: 'SKU',
        Cell: ({ row }) => {
          const item = row.original.inventoryItem;
          return item ? `${item.upc}${item.name ? ` — ${item.name}` : ''}` : row.original.inventoryItemId;
        },
      },
      { accessorKey: 'onHandQty', header: 'On hand' },
      { accessorKey: 'openPoQty', header: 'Open PO' },
      { accessorKey: 'effectiveAvail', header: 'Effective' },
      { accessorKey: 'reorderPoint', header: 'Reorder pt' },
      { accessorKey: 'targetQty', header: 'Target' },
      { accessorKey: 'suggestedBuyQty', header: 'Buy qty' },
      {
        accessorKey: 'reason',
        header: 'Reason',
        Cell: ({ cell }) =>
          cell.getValue<string>() ? (
            <Chip size="small" label={String(cell.getValue()).replace(/_/g, ' ')} color="warning" variant="outlined" />
          ) : null,
      },
      {
        id: 'actions',
        header: '',
        Cell: ({ row }) => (
          <Button size="small" variant="outlined" onClick={() => void onCreatePo(row.original)} disabled={row.original.suggestedBuyQty <= 0}>
            Create PO
          </Button>
        ),
      },
    ],
    [onCreatePo],
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Buying suggestions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Based on replenishment policy: effective availability = on hand + open PO lines. Suggested buy = target − effective.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          select
          size="small"
          label="Location"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All locations</MenuItem>
          {locations.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.name ?? `Location ${l.id}`}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={triggerOnly} onChange={(_, v) => setTriggerOnly(v)} />}
          label="Below reorder only"
        />
        <Button variant="outlined" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <MaterialReactTable
        columns={columns}
        data={rows}
        state={{ isLoading: loading }}
        enablePagination
        initialState={{ pagination: { pageSize: 25, pageIndex: 0 } }}
      />
    </Paper>
  );
};

export default BuyingSuggestionsPanel;
